import "./style.css";
import dayjs from "dayjs";
import { LeftCircleOutlined, RightCircleOutlined } from "@ant-design/icons";
import placeHolderImage from "./../../../assets/images/placeHolder.svg";
import { Col, Row, Card, Carousel } from "antd";
import React, { useCallback, useEffect, useState } from "react";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { setVehicleDetails } from "../../../reducers/eolSlice";
import { getApis } from "../../Common/apis";
import {
  vehicleDetailsApiUrl,
  vehicleHealthStatusUrl,
} from "../../Common/endpoints";
import { useRequiredAccess } from "../../../hooks/useRequiredAccess";
import { ORG_TYPES } from "../../Common/Constants";
import { setPageLoader } from "../../../reducers/loaderSlice";
import VehicleHealthTable from "./VehicleHealthTable";
import LiveDateTime from "../../Common/LiveDateTime";
import VehicleDetails from "../VehicleDetails";

const statusColorCode = {
  Poor: "#ff0000",
  Average: "#F07900",
  Good: "#ffff04",
  Excellent: "#04ff00",
};

const VehicleHealth = () => {
  const dispatch = useDispatch();
  const vehicleDetails = useSelector((state) => state.eol.vehicleDetails);
  const [vehicle, setVehicle] = useState(null);
  const [partsHealth, setPartsHealth] = useState([]);
  const [partsHovered, setPartsHovered] = useState({});

  const authToken = localStorage.getItem("authToken");
  const checkAccess = useRequiredAccess(
    [],
    [ORG_TYPES.ADMINISTRATOR, ORG_TYPES.MANUFACTURER]
  );

  const getVehicleDetails = useCallback(async () => {
    try {
      const response = await getApis(
        vehicleDetailsApiUrl,
        {
          imei: vehicle?.vehImei,
        },
        authToken
      );
      dispatch(setVehicleDetails(response?.data));
      // dispatch(setPageLoader(null));
    } catch (e) {
      toast.error("Error Fetching Vehicle Details!");
      console.log("API Failed: vehicleDetailsApi: ", e.response?.data?.message);
    }
  }, [authToken, dispatch, vehicle?.vehImei]);

  const getVehicleHealthStatus = useCallback(async () => {
    try {
      const response = await getApis(
        vehicleHealthStatusUrl,
        {
          imei: vehicle?.vehImei,
        },
        authToken
      );
      if (response?.data?.parts) {
        setPartsHealth(response?.data?.parts);
        let partInfo = {};
        const partStatus = response?.data?.parts?.reduce((acc, field) => {
          acc[field?.partType] = field?.status;
          partInfo[field?.partType] = field?.info;
          return acc;
        }, {});
        setPartsHovered({ status: partStatus, info: partInfo });
      } else {
        setPartsHealth([]);
      }
    } catch (e) {
      toast.error("Error Fetching Health Status!");
      console.log(
        "API Failed: vehicleHealthStatus: ",
        e.response?.data?.message
      );
    }
  }, [authToken, vehicle?.vehImei]);

  useEffect(() => {
    checkAccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (vehicle?.vehImei && vehicle?.vehImei !== "") {
      dispatch(setPageLoader("refresh"));
      Promise.allSettled([getVehicleDetails(), getVehicleHealthStatus()])
        .then((results) => {
          results.forEach((result) => {
            if (result.status !== "fulfilled") {
              console.log("API Failed: ", result.status);
            }
          });
        })
        .finally(() => {
          dispatch(setPageLoader(null));
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getVehicleDetails, getVehicleHealthStatus, vehicle?.vehImei]);

  const onChangeImei = (_, value) => {
    setVehicle(value);
    // dispatch(setVehicleDetails({}));
  };

  const partMouseOver = (e) => {
    setPartsHovered((prev) => ({
      ...prev,
      [e.target.id]: true,
      activePart: e.target.id,
      activeInfo: prev?.info?.[e.target.id],
    }));
  };

  const partMouseOut = () => {
    setPartsHovered((prev) => ({ status: prev.status, info: prev.info }));
  };

  const handleMouseEnterRow = (rowindex, values) => {
    setPartsHovered((prev) => ({
      ...prev,
      [values?.partType]: true,
      activePart: values?.partType,
      activeInfo: prev?.info?.[values?.partType],
    }));
  };

  const handleMouseLeaveRow = () => {
    setPartsHovered((prev) => ({ status: prev.status, info: prev.info }));
  };

  const onCarouselChange = (viewPos) => {};

  return (
    <div data-testid="vehicle-health" className="vehicle-health-page">
      <Row gutter={[16, 16]} className="mt1">
        <Col xs={24} sm={14} md={12} lg={10} xl={7}>
          <div className="lable">Vehicle ID</div>
          <VehicleDetails
            onChangeImei={onChangeImei}
            imei={vehicle}
            card={false}
          />
        </Col>
        <Col xs={24} sm={10} md={12} lg={14} xl={17} className="justify-right">
          <LiveDateTime />
        </Col>
      </Row>
      <Row gutter={[16, 16]} className="mt1 mb1">
        <Col xs={24} sm={24} md={14} lg={12} xl={12}>
          <Row gutter={[16, 16]} className="h100">
            <Col span={24} className="text-center vehicle-part-image-col">
              {vehicleDetails?.profileImages &&
              Object.keys(vehicleDetails?.profileImages).length ? (
                <Carousel
                  arrows
                  dots={false}
                  afterChange={onCarouselChange}
                  prevArrow={<LeftCircleOutlined />}
                  nextArrow={<RightCircleOutlined />}
                >
                  {Object.keys(vehicleDetails?.profileImages)?.map(
                    (img, index) => (
                      <div key={index} className="vehicle-part-image-box">
                        <img
                          key={index}
                          alt="Vehicle"
                          src={vehicleDetails?.profileImages[img].url}
                          className="vehicle-part-image"
                        />
                        <svg className="vehicle-part-image-overlay">
                          {vehicleDetails?.profileImages[
                            img
                          ]?.partListCoordinates?.map((part, index) => {
                            if (part?.partLabel && part?.x && part?.y) {
                              return statusColorCode[
                                partsHovered?.status?.[part?.partLabel]
                              ] ? (
                                <circle
                                  key={index}
                                  id={part?.partLabel}
                                  cx={part?.x}
                                  cy={part?.y}
                                  r={
                                    partsHovered[part?.partLabel] &&
                                    partsHovered?.activePart === part?.partLabel
                                      ? 14
                                      : 9
                                  }
                                  fill={
                                    statusColorCode[
                                      partsHovered?.status?.[part?.partLabel]
                                    ] || "#6897bb"
                                  }
                                  onMouseOver={(e) => partMouseOver(e)}
                                  onMouseOut={partMouseOut}
                                />
                              ) : (
                                <>
                                  <circle
                                    key={index}
                                    id={part?.partLabel}
                                    cx={part?.x}
                                    cy={part?.y}
                                    r={
                                      partsHovered[part?.partLabel] &&
                                      partsHovered?.activePart ===
                                        part?.partLabel
                                        ? 10
                                        : 9
                                    }
                                    onMouseOver={(e) => partMouseOver(e)}
                                    onMouseOut={partMouseOut}
                                    stroke="red"
                                    stroke-width="2"
                                    fill="white"
                                  />
                                  <rect
                                    x={part?.x - 1}
                                    y={part?.y - 4}
                                    width="2"
                                    height="6"
                                    fill="red"
                                    id={part?.partLabel}
                                    onMouseOver={(e) => partMouseOver(e)}
                                    onMouseOut={partMouseOut}
                                  />
                                  <rect
                                    x={part?.x - 1}
                                    y={part?.y + 3}
                                    width="2"
                                    height="2"
                                    fill="red"
                                    id={part?.partLabel}
                                    onMouseOver={(e) => partMouseOver(e)}
                                    onMouseOut={partMouseOut}
                                  />
                                </>
                              );
                            } else {
                              return null;
                            }
                          })}
                        </svg>
                      </div>
                    )
                  )}
                </Carousel>
              ) : (
                <div className="vehicle-part-image-box">
                  <img
                    alt="Vehicle"
                    width="400"
                    height="400"
                    src={placeHolderImage}
                    className="vehicle-part-image"
                  />
                </div>
              )}
            </Col>
            <Col span={24} className="text-center">
              <div className="vehicle-number-title w100">Vehicle Number</div>
              <div className="vehicle-number w100">
                {vehicleDetails?.regNo || "XX XX XX 0000"}
              </div>
            </Col>
            <Col span={12}>
              <Card className="align-center pd05">
                <div>Odometer</div>
                {vehicleDetails?.odometer ? (
                  <div className="bold mt05">{vehicleDetails?.odometer}</div>
                ) : (
                  <div className="disabled mt05">Not Available</div>
                )}
              </Card>
            </Col>
            <Col span={12}>
              <Card className="align-center pd05">
                <div>Sale Date</div>
                {vehicleDetails?.saleDate ? (
                  <div className="bold mt05">
                    {dayjs(vehicleDetails?.saleDate).format("Do MMMM YYYY")}
                  </div>
                ) : (
                  <div className="disabled mt05">Not Available</div>
                )}
              </Card>
            </Col>
          </Row>
        </Col>

        <Col xs={24} sm={24} md={10} lg={12} xl={12}>
          <Row className="white-bg border h100 p0">
            <VehicleHealthTable
              vehiclePartList={partsHealth}
              partsHovered={partsHovered}
              handleMouseEnterRow={handleMouseEnterRow}
              handleMouseLeaveRow={handleMouseLeaveRow}
            />
            <Col span={24} className="align-end ">
              {partsHovered?.activePart ? (
                <div className="summaries border w100">
                  {partsHovered?.activeInfo ? (
                    <>
                      <div span={24} className="title">
                        {partsHovered.activePart.slice(0, 1) +
                          partsHovered.activePart
                            .slice(1, partsHovered.activePart.length)
                            .toLowerCase()}
                        {partsHovered.activePart === "TYRE" ? " Pressure" : ""}
                      </div>
                      <div span={24} className="mt05 text-justify desc">
                        &nbsp; &nbsp; &nbsp;{partsHovered.activeInfo}
                      </div>
                    </>
                  ) : (
                    <div span={24} className="disabled head18 plr05">
                      <ExclamationCircleOutlined
                        className="mr025"
                        style={{
                          fontSize: "18px",
                          color: "#bcbcbc",
                          padding: "8px",
                        }}
                      />
                      <span
                        style={{
                          fontSize: "16px",
                          color: "#bcbcbc",
                        }}
                      >
                        Part health is not available currently
                      </span>
                    </div>
                  )}
                </div>
              ) : null}
            </Col>
          </Row>
        </Col>
      </Row>
    </div>
  );
};

export default VehicleHealth;
